import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { withCurrenciesInUse } from '@norvix/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useOfferedCurrencies } from '../../api/currencies.api.js';
import {
  PROVIDER_FEE_OPERATIONS,
  providerFeesApi,
  type FeeType,
  type ProviderFeeEstimate,
} from '../../api/fees.api.js';
import { useCanWriteSectionPart } from '../settings/useSettingsAccess.js';

import { describeFeeType, FeeTypeBuilder, formatCents } from './FeeTypeBuilder.js';

/**
 * Provider costs (DEV-127): what GuruPay charges Norvix per operation.
 *
 * Versioned like customer pricing. Used only to ESTIMATE what an operation
 * cost us until GuruPay's own statement shows the actual charge — nothing is
 * posted from it.
 */

const PROVIDER = 'GURUPAY';

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'The request failed.';
}

function CreateProviderCostDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  // DEV-162: Settings → Organisation → Currencies offered.
  const offered = useOfferedCurrencies();
  const [operation, setOperation] = useState<string>('SEPA_OUT');
  const [currency, setCurrency] = useState('EUR');
  const [feeType, setFeeType] = useState<FeeType>({ kind: 'FLAT', amountCents: '0' });
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 16));
  const [reason, setReason] = useState('');
  const create = useMutation({
    mutationFn: () =>
      providerFeesApi.create({
        provider: PROVIDER,
        operation,
        currency,
        feeType,
        effectiveFrom: new Date(effectiveFrom).toISOString(),
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['provider-fee-schedules'] });
      setReason('');
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New GuruPay price version</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {create.isError ? <Alert severity="error">{errorText(create.error)}</Alert> : null}
          <TextField select label="Operation" value={operation} onChange={(e) => setOperation(e.target.value)}>
            {PROVIDER_FEE_OPERATIONS.map((op) => (
              <MenuItem key={op} value={op}>
                {op}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {withCurrenciesInUse(offered.currencies, [currency]).map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <FeeTypeBuilder value={feeType} onChange={setFeeType} />
          <TextField
            label="Effective from"
            type="datetime-local"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Closes the current version for this operation and currency at this moment."
          />
          <TextField
            label="Reason (e.g. the tariff sheet it comes from)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={reason.trim().length < 4 || create.isPending}
          onClick={() => create.mutate()}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EstimateTool(): JSX.Element {
  const offered = useOfferedCurrencies();
  const [operation, setOperation] = useState<string>('SEPA_OUT');
  const [currency, setCurrency] = useState('EUR');
  const [amountCents, setAmountCents] = useState('10000');
  const [result, setResult] = useState<ProviderFeeEstimate | null>(null);
  const estimate = useMutation({
    mutationFn: () => providerFeesApi.estimate({ provider: PROVIDER, operation, currency, amountCents }),
    onSuccess: setResult,
    onError: () => setResult(null),
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Operation" value={operation} onChange={(e) => setOperation(e.target.value)} sx={{ minWidth: 180 }}>
          {PROVIDER_FEE_OPERATIONS.map((op) => (
            <MenuItem key={op} value={op}>
              {op}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ minWidth: 100 }}>
          {withCurrenciesInUse(offered.currencies, [currency]).map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Amount (cents)" type="number" value={amountCents} onChange={(e) => setAmountCents(e.target.value)} />
        <Button variant="outlined" onClick={() => estimate.mutate()} disabled={estimate.isPending}>
          Estimate
        </Button>
      </Stack>
      {estimate.isError ? <Alert severity="warning" sx={{ mt: 1 }}>{errorText(estimate.error)}</Alert> : null}
      {result !== null ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Estimated GuruPay cost for {formatCents(result.amountCents, currency)}:{' '}
          <strong>{formatCents(result.estimatedFeeCents, currency)}</strong> (price v{result.version})
        </Typography>
      ) : null}
    </Box>
  );
}

export function ProviderCostsSection(): JSX.Element {
  // What the provider charges us: read by everyone on the pricing page, changed by admin only.
  const canEdit = useCanWriteSectionPart({ group: 'pricing', id: 'fees' }, 'provider-costs');
  const [createOpen, setCreateOpen] = useState(false);
  const schedules = useQuery({
    queryKey: ['provider-fee-schedules'],
    queryFn: () => providerFeesApi.list(PROVIDER),
  });

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">Provider costs — GuruPay</Typography>
          <Box flex={1} />
          {canEdit ? <Button variant="outlined" onClick={() => setCreateOpen(true)}>
            New price version
          </Button> : null}
        </Stack>
        <Alert severity="info" sx={{ mt: 2 }}>
          What GuruPay charges Norvix per operation. These are estimates only: the actual charges come
          from GuruPay’s statement, and nothing is posted to any account from this table.
        </Alert>
        {schedules.isError ? <Alert severity="error" sx={{ mt: 2 }}>{errorText(schedules.error)}</Alert> : null}
        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Operation</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Effective from</TableCell>
              <TableCell>Effective to</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(schedules.data ?? []).map((s) => (
              <TableRow key={s.id}>
                <TableCell>{s.operation}</TableCell>
                <TableCell>{s.currency}</TableCell>
                <TableCell>v{s.version}</TableCell>
                <TableCell>{describeFeeType(s.feeType)}</TableCell>
                <TableCell>{s.effectiveFrom.slice(0, 16).replace('T', ' ')}</TableCell>
                <TableCell>{s.effectiveTo?.slice(0, 16).replace('T', ' ') ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={s.state === 'IN_FORCE' ? 'In force' : s.state === 'SCHEDULED' ? 'Scheduled' : 'Ended'}
                    color={s.state === 'IN_FORCE' ? 'success' : 'default'}
                  />
                </TableCell>
              </TableRow>
            ))}
            {schedules.data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No GuruPay prices recorded yet.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <EstimateTool />
      </CardContent>
      <CreateProviderCostDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Card>
  );
}
